import io from 'socket.io-client'
import store from './store/store'

// Import actions and thunks
import {
  addMessageToConversation,
  updateMessageStatus,
  updateMessageDeliveryStatus,
  markConversationRead,
  setCurrentConversation,
  setUserTyping
} from './store/API/messageSlice'

import {
  deleteMessageThunk,
  pinMessageThunk,
  getConversationsThunk
} from './store/API/messageSlice'

import { getAllBookings } from './store/slices/bookingSlice'
import { fetchTransactions } from './store/slices/generalTransactionSlice'
import {
  addNotification,
  addRealTimeNotification
} from './store/slices/notificationSlice'

let socket

// UPDATED: Include port and proper configuration
const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL || 'https://alnoordevelopers.com'

export const initSocket = () => {
  // Prevent multiple connections
  if (socket && socket.connected) {
    console.log('Socket Service: Already connected.')
    return
  }

  // Disconnect previous socket if exists but not connected properly
  if (socket) {
    socket.disconnect()
  }

  // Get token from sessionStorage
  const token = sessionStorage.getItem('token')

  if (!token) {
    console.log(
      'Socket Service: No auth token found in sessionStorage, cannot connect.'
    )
    return
  }

  console.log('Socket Service: Initializing connection to:', SOCKET_URL)
  socket = io(SOCKET_URL, {
    auth: {
      token: token
    },
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
    // Cloudflare's proxy doesn't reliably support the websocket upgrade probe
    // for this connection; stay on polling, which already works fine.
    transports: ['polling'],
    upgrade: false,
    // Add path if using a custom path (default is /socket.io)
    path: '/socket.io/',
    // Enable secure connection
    secure: true,
    // Reject unauthorized certificates (set to false only for development)
    rejectUnauthorized: true
  })

  // --- Connection Event Handlers ---
  socket.on('connect', () => {
    console.log(`Socket Service: Connected with ID: ${socket.id}`)
    console.log('Socket Service: Transport:', socket.io.engine.transport.name)

    // Listen for transport upgrade
    socket.io.engine.on('upgrade', transport => {
      console.log('Socket Service: Upgraded to:', transport.name)
    })

    if (socket.userId) {
      localStorage.setItem('userId', socket.userId)
    }
  })

  socket.on('disconnect', reason => {
    console.log(`Socket Service: Disconnected. Reason: ${reason}`)
    if (reason === 'io server disconnect') {
      console.log('Socket Service: Server disconnected the client')
    }
  })

  socket.on('connect_error', error => {
    console.error(`Socket Service: Connection Error:`, error)
    console.error('Socket Service: Error message:', error.message)
    console.error('Socket Service: Error description:', error.description)

    if (
      error.message.includes('Authentication') ||
      error.message.includes('Invalid token')
    ) {
      console.log('Socket Service: Authentication failed, disconnecting')
      if (socket) {
        socket.disconnect()
        socket = null
      }
    }
  })

  socket.on('reconnect_attempt', attemptNumber => {
    console.log(`Socket Service: Reconnection attempt #${attemptNumber}`)
  })

  socket.on('reconnect_error', error => {
    console.error('Socket Service: Reconnection error:', error.message)
  })

  socket.on('reconnect_failed', () => {
    console.error('Socket Service: Reconnection failed after all attempts')
  })

  // --- Custom Event Listeners ---

  socket.on('newMessage', message => {
    console.log('Socket Service: Received message:', message)
    if (message && message.data && message.conversationId) {
      store.dispatch(
        addMessageToConversation({
          conversationId: message.conversationId,
          message: message.data
        })
      )

      const state = store.getState()
      if (state.messages.currentConversationId === message.conversationId) {
        socket.emit('messageDelivered', {
          messageId: message.data._id,
          conversationId: message.conversationId
        })
      }

      window.dispatchEvent(new CustomEvent('newMessage', { detail: message }))
    }
  })

  socket.on('messageStatusUpdated', data => {
    console.log('Socket Service: Message status updated:', data)
    if (data && data.messageId && data.conversationId && data.status) {
      store.dispatch(
        updateMessageStatus({
          messageId: data.messageId,
          status: data.status
        })
      )
    }
  })

  socket.on('messageDeliveryUpdate', data => {
    console.log('Socket Service: Message delivery updated:', data)
    if (data && data.messageId && data.status) {
      store.dispatch(
        updateMessageDeliveryStatus({
          messageId: data.messageId,
          userId: data.userId,
          status: data.status
        })
      )
    }
  })

  socket.on('conversationRead', data => {
    console.log('Socket Service: Conversation marked as read:', data)
    if (data && data.conversationId && data.userId !== undefined) {
      store.dispatch(
        markConversationRead({
          conversationId: data.conversationId,
          userId: data.userId,
          unreadCount: data.unreadCount || 0
        })
      )

      window.dispatchEvent(
        new CustomEvent('conversationRead', { detail: data })
      )
    }
  })

  socket.on('messagesRead', data => {
    console.log('Socket Service: Messages marked as read by other user:', data)
    if (data && data.conversationId) {
      store.dispatch(getConversationsThunk())
    }
  })

  socket.on('messageDeleted', data => {
    console.log('Socket Service: Message deleted:', data)
    if (data && data.messageId) {
      store.dispatch(deleteMessageThunk(data.messageId))
    }
  })

  socket.on('messagePinned', data => {
    console.log('Socket Service: Message pinned/unpinned:', data)
    if (data && data.messageId) {
      store.dispatch(
        pinMessageThunk({
          messageId: data.messageId,
          pin: data.isPinned
        })
      )
    }
  })

  socket.on('typing', data => {
    if (data && data.conversationId && data.senderId) {
      store.dispatch(
        setUserTyping({
          conversationId: data.conversationId,
          userId: data.senderId,
          isTyping: data.isTyping
        })
      )

      window.dispatchEvent(
        new CustomEvent('userTyping', {
          detail: {
            conversationId: data.conversationId,
            userId: data.senderId,
            isTyping: data.isTyping
          }
        })
      )
    }
  })

  socket.on('messageUpdated', message => {
    console.log('Socket Service: Message updated:', message)
    if (message && message._id && message.conversationId) {
      window.dispatchEvent(
        new CustomEvent('messageUpdated', { detail: message })
      )
    }
  })

  socket.on('bookingChanged', data => {
    console.log('Socket Service: Booking changed:', data)
    store.dispatch(getAllBookings())

    try {
      const { fetchAggregation } = require('./store/slices/aggregationSlice')
      const state = store.getState()
      const selectedSocietyId = state?.societies?.selectedSocietyId
      if (selectedSocietyId) {
        store.dispatch(fetchAggregation(selectedSocietyId))
      }
    } catch (e) {}
  })

  socket.on('plotUpdated', data => {
    console.log('Socket Service: Plot updated:', data)
    // Dispatch window event for components that need immediate plot updates
    window.dispatchEvent(
      new CustomEvent('plotUpdated', { detail: data })
    )
    // Refresh societies to update plot status in real-time
    try {
      const { fetchSocieties } = require('./store/slices/societySlice')
      store.dispatch(fetchSocieties())
    } catch (e) {
      console.error('Error refreshing societies on plot update:', e)
    }
  })

  socket.on('holdBookingsExpired', data => {
    console.log('Socket Service: Hold bookings expired:', data)
    // Refresh hold bookings lists
    try {
      const { getHoldBookings, getExpiredHoldBookings } = require('./store/slices/bookingSlice')
      store.dispatch(getHoldBookings())
      store.dispatch(getExpiredHoldBookings())
    } catch (e) {
      console.error('Error refreshing hold bookings:', e)
    }
    // Also refresh societies to update plot status
    try {
      const { fetchSocieties } = require('./store/slices/societySlice')
      store.dispatch(fetchSocieties())
    } catch (e) {
      console.error('Error refreshing societies on hold booking expiry:', e)
    }
  })

  socket.on('transactionChanged', data => {
    console.log('Socket Service: Transaction changed:', data)
    store.dispatch(fetchTransactions())

    try {
      const { fetchAggregation } = require('./store/slices/aggregationSlice')
      const state = store.getState()
      const selectedSocietyId = state?.societies?.selectedSocietyId
      if (selectedSocietyId) {
        store.dispatch(fetchAggregation(selectedSocietyId))
      }
    } catch (e) {}
  })

  socket.on('userChanged', data => {
    console.log('Socket Service: User changed:', data)
    try {
      const { fetchAllUsers } = require('./store/slices/authSlice')
      store.dispatch(fetchAllUsers())
    } catch (e) {}
  })

  socket.on('notification', notification => {
    console.log('Socket Service: Received notification:', notification)
    store.dispatch(addRealTimeNotification(notification))

    window.dispatchEvent(
      new CustomEvent('newNotification', { detail: notification })
    )

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.type, {
        body: notification.message,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: notification._id
      })
    }
  })
}

// --- Emit Events ---

export const emitTyping = (conversationId, recipientId, isTyping) => {
  if (socket && socket.connected) {
    socket.emit('typing', { conversationId, recipientId, isTyping })
  } else {
    console.warn('Socket not connected, cannot emit typing event.')
  }
}

export const emitMarkAsRead = conversationId => {
  if (socket && socket.connected) {
    console.log(`Socket Service: Emitting markAsRead for ${conversationId}`)
    socket.emit('markConversationRead', { conversationId })
    socket.emit('markAsRead', { conversationId })
  } else {
    console.warn('Socket not connected, cannot emit markAsRead event.')
  }
}

export const emitMessageDelivered = (messageId, conversationId) => {
  if (socket && socket.connected) {
    console.log(`Socket Service: Emitting messageDelivered for ${messageId}`)
    socket.emit('messageDelivered', { messageId, conversationId })
  } else {
    console.warn('Socket not connected, cannot emit messageDelivered event.')
  }
}

export const emitJoinConversation = conversationId => {
  if (socket && socket.connected) {
    console.log(`Socket Service: Joining conversation ${conversationId}`)
    socket.emit('joinConversation', conversationId)
  } else {
    console.warn('Socket not connected, cannot join conversation.')
  }
}

export const emitLeaveConversation = conversationId => {
  if (socket && socket.connected) {
    console.log(`Socket Service: Leaving conversation ${conversationId}`)
    socket.emit('leaveConversation', conversationId)
  } else {
    console.warn('Socket not connected, cannot leave conversation.')
  }
}

export const disconnectSocket = () => {
  if (socket) {
    console.log('Socket Service: Disconnecting...')
    socket.disconnect()
    socket = null
  }
}

export const isSocketConnected = () => {
  return socket && socket.connected
}

export const getSocketId = () => {
  return socket ? socket.id : null
}
